import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
    message: string;
    type: NotificationType;
    visible: boolean;
}

interface NotificationContextType {
    notification: Notification;
    showNotification: (message: string, type?: NotificationType, duration?: number) => void;
    hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
    const [notification, setNotification] = useState<Notification>({
        message: '',
        type: 'success',
        visible: false,
    });

    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

    const showNotification = useCallback((message: string, type: NotificationType = 'success', duration: number = 5000) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        setNotification({ message, type, visible: true });

        const id = setTimeout(() => {
            setNotification(prev => ({ ...prev, visible: false }));
        }, duration);
        setTimeoutId(id);
    }, [timeoutId]);

    const hideNotification = useCallback(() => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        setNotification(prev => ({ ...prev, visible: false }));
    }, [timeoutId]);

    return (
        <NotificationContext.Provider value={{ notification, showNotification, hideNotification }}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotification() {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
}
